import React, { useMemo, useState } from "react"

import {
  getBinaryAggregateColor,
  getMetricColorForMetric,
  getMetricTextColorClass,
  getMetricType,
  METRIC_TYPE,
  MetricVersionSummaryTag,
} from "@/metrics/metric-display"
import { getSkippedExpectedBehaviorMessage } from "@/metrics/registry"
import type { MetricRenderAdapter } from "@/metrics/runtime-adapters"
import { Checkbox } from "@coval/ui"

import { useToggleWithoutSelection } from "@/hooks/useToggleWithoutSelection"
import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import type { CompareBy } from "../../compare-by"
import { useHighlightValue } from "../hooks/useHighlightValue"

interface MetricCardSectionProps {
  runRows: RunRowLike[]
  metricRecord: MetricRenderAdapter
  onSelectMetric: (metricId: string) => void
  /** When set (e.g. multi-run compare by agent/persona), card uses getComparedOverviewNode when available. */
  compareBy?: CompareBy | null
}

/**
 * Renders a single metric card. Holds its own "include unknowns" state when applicable.
 */
export const MetricCardSection: React.FC<MetricCardSectionProps> = ({
  runRows,
  metricRecord,
  onSelectMetric,
  compareBy = null,
}) => {
  const [includeUnknowns, setIncludeUnknowns] = useState(true)

  // Select the metric on a genuine click, but not while selecting text.
  const toggleHandlers = useToggleWithoutSelection(() =>
    onSelectMetric(metricRecord.id),
  )

  const highlightValue = useHighlightValue(
    runRows,
    metricRecord.id,
    metricRecord,
  )

  const metricOutputs = useMemo<RunMetricOutput[]>(
    () =>
      runRows
        .map((row) => row.metric_outputs?.[metricRecord.id])
        .filter(
          (output): output is RunMetricOutput =>
            output !== undefined &&
            (output.error_status === MetricErrorStatus.COMPLETED ||
              !output.error_status),
        ),
    [runRows, metricRecord],
  )

  // Pre-compute per-branch stats so they're memoized and not recalculated on every render.
  const floatMean = useMemo(() => {
    const valid = metricOutputs.filter(
      (m) => m.float_value != null && Number.isFinite(m.float_value),
    )
    if (valid.length === 0) {
      return null
    }
    return (
      valid.reduce((s, m) => s + (m.float_value as number), 0) / valid.length
    )
  }, [metricOutputs])

  if (!metricRecord) {
    return null
  }

  if (metricOutputs.length === 0) {
    const emptyMessage = getSkippedExpectedBehaviorMessage(
      metricRecord,
      runRows,
    )
    return emptyMessage ? (
      <div className="p-4 space-y-2">
        <h3 className="text-sm font-semibold text-coval-text">
          {resolveMetricDisplayName(
            metricRecord.metric_display_name,
            metricRecord.metric_name,
          )}
        </h3>
        <p className="text-sm text-coval-text-muted">{emptyMessage}</p>
      </div>
    ) : null
  }

  const metricId = metricRecord.id

  const isBinaryWithUnknowns = metricRecord.hasBinaryUnknowns(metricOutputs)

  const getHeaderStatSpanClass = (color: "default" | "success" | "danger") =>
    `whitespace-nowrap ${getMetricTextColorClass(color)}`

  let displayValueNode: React.ReactNode
  let headerValue: string | undefined
  let headerValueNode: React.ReactNode | undefined
  let subtitleNode: React.ReactNode | undefined
  const isBinary = getMetricType(metricRecord) === METRIC_TYPE.BINARY
  let headerStatColor: "default" | "success" | "danger" = isBinary
    ? getBinaryAggregateColor(
        metricRecord.calculateBinaryPassRate(
          metricOutputs,
          isBinaryWithUnknowns && includeUnknowns,
        ),
        metricRecord,
      )
    : "default"

  if (isBinaryWithUnknowns) {
    const binaryRecord = metricRecord
    const binaryOverview = binaryRecord.getOverviewNode(metricOutputs, {
      includeUnknowns,
      onIncludeUnknownsChange: setIncludeUnknowns,
      hideCheckbox: true,
    })
    const comparedNode = compareBy
      ? binaryRecord.getComparedOverviewNode(runRows, compareBy)
      : null
    displayValueNode = comparedNode ?? binaryOverview
    const headerPercent = metricRecord.calculateBinaryHeaderPercent(
      metricOutputs,
      includeUnknowns,
    )
    headerValueNode = headerPercent ? (
      <span className={getHeaderStatSpanClass(headerStatColor)}>
        {headerPercent}
        {headerStatColor === "danger" && (
          <span className="sr-only">(below target)</span>
        )}
      </span>
    ) : undefined
    subtitleNode = (
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <label className="flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={includeUnknowns}
            onCheckedChange={(checked) => setIncludeUnknowns(!!checked)}
          />
          <span className="text-[10px] text-coval-text-muted">
            Include Unknowns in total
          </span>
        </label>
      </div>
    )
  } else {
    if (!isBinary && floatMean !== null) {
      headerStatColor = getMetricColorForMetric(floatMean, metricRecord)
    }
    const comparedNode = compareBy
      ? metricRecord.getComparedOverviewNode(runRows, compareBy)
      : null
    displayValueNode =
      comparedNode ??
      metricRecord.getOverviewNode(metricOutputs, { highlightValue })
    headerValue = metricRecord.getHeaderValue?.(metricOutputs) ?? undefined
  }

  const cardTitle = resolveMetricDisplayName(
    metricRecord.metric_display_name,
    metricRecord.metric_name,
  )
  const cardDescription = `Average across ${metricOutputs.length} simulated conversations`

  return (
    // The whole card toggles, but text stays selectable: the click handler
    // ignores drag-selections and live selections, and the value region below
    // stops propagation so it never toggles.
    <div
      className="pt-4 pb-4 px-4 cursor-pointer relative group flex flex-col focus-visible:outline-none"
      role="button"
      tabIndex={0}
      aria-label={`${cardTitle}: ${cardDescription}`}
      {...toggleHandlers}
      onKeyDown={(e) => {
        // Keys pressed on a control inside the card, such as a chart's table
        // toggle, belong to that control.
        if (e.target !== e.currentTarget) {
          return
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelectMetric(metricId)
        }
      }}
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="text-sm font-semibold text-coval-text wrap-break-word"
              title={cardTitle}
            >
              {cardTitle}
            </div>
            <MetricVersionSummaryTag
              metric={metricRecord}
              metricOutputs={metricOutputs}
            />
          </div>
          {subtitleNode}
        </div>
        {(headerValue != null || headerValueNode != null) && (
          <div className="flex items-center gap-2 shrink-0 text-right text-2xl font-bold text-coval-text">
            {headerValueNode != null
              ? headerValueNode
              : headerValue != null && (
                  <span className={getHeaderStatSpanClass(headerStatColor)}>
                    {headerValue}
                    {headerStatColor === "danger" && (
                      <span className="sr-only">(below target)</span>
                    )}
                  </span>
                )}
          </div>
        )}
      </div>
      {displayValueNode != null && (
        <div
          role="presentation"
          className="min-w-0 mt-2 text-base font-semibold text-coval-text"
          title={cardDescription}
          // Value prose stays selectable and never toggles from clicks here.
          onClick={(e) => e.stopPropagation()}
        >
          {displayValueNode}
        </div>
      )}
    </div>
  )
}
